import React, { useEffect, useState, useMemo, useCallback } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import SegmentedTabs from '../components/ui/SegmentedTabs';
import {
  B2_ASPEKTE_CHAPTERS,
  VOCAB_SETS,
  getCourses,
  getLessons,
  getEntries,
  getStats,
  shuffleArray,
} from '../data/vocab';
import {
  BookOpen,
  Search,
  RotateCw,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ListFilter,
  GraduationCap,
  Bookmark,
  Layers,
  ArrowRight,
  Trophy,
  X,
  Download,
} from 'lucide-react';
import { downloadVocabPdf } from '../utils/vocabPdf';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { unknownWordsAPI } from '../services/api';
import { useQuery, STALE } from '../hooks/useQuery';
import { makeKey, setQueryData, invalidateQueries } from '../lib/queryCache';
import WordDetailsModal from '../components/Vocabulary/WordDetailsModal';
import SpeakButton from '../components/ui/SpeakButton';
import { reviewWord, GRADE_LABELS, SRS_GRADES } from '../utils/srsEngine';

const VOCAB_PAGE_SIZE = 30;

// Select with its label inside the field ("Leksioni  Të gjitha  v").
const FilterSelect = ({ label, value, onChange, disabled = false, children }) => (
  <label
    className={cn(
      'relative flex h-11 min-w-0 items-center gap-2 rounded-xl border border-zinc-200 bg-white pl-3.5 shadow-sm transition hover:border-zinc-300 focus-within:border-[#DD0000]/50 focus-within:ring-4 focus-within:ring-[#DD0000]/10',
      disabled && 'cursor-not-allowed opacity-60 hover:border-zinc-200',
    )}
  >
    <span className="shrink-0 text-xs text-zinc-400">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-full min-w-0 flex-1 cursor-pointer appearance-none truncate bg-transparent pr-9 text-sm font-medium text-zinc-900 focus:outline-none disabled:cursor-not-allowed"
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-zinc-400" />
  </label>
);

const FilterChip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000]/30 ${
      active
        ? 'border-zinc-950 bg-zinc-950 text-[#FFCE00]'
        : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50'
    }`}
  >
    {children}
  </button>
);


const KnownWordToggle = ({ isKnown, disabled, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={isKnown}
    title={isKnown ? 'E shënuar si e ditur (Kliko për ta hequr)' : 'E di këtë fjalë (Kliko për ta shënuar si të ditur)'}
    aria-label={isKnown ? 'E ditur' : 'Shëno si të ditur'}
    className={cn(
      'shrink-0 inline-flex items-center justify-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-bold transition-all cursor-pointer disabled:cursor-default select-none',
      isKnown
        ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 shadow-2xs'
        : 'border border-zinc-200 bg-white text-zinc-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50/50',
      className
    )}
  >
    <CheckCircle2 className={cn('h-4 w-4 shrink-0', isKnown ? 'text-emerald-600 fill-emerald-100' : 'text-zinc-400')} />
    <span className="hidden sm:inline">{isKnown ? 'E di' : 'E di?'}</span>
  </button>
);

const MODES = [
  { id: 'browse', label: 'Lista e Fjalëve', icon: ListFilter },
  { id: 'flashcards', label: 'Flashcards', icon: BookOpen },
  { id: 'quiz', label: 'Kuiz Praktik', icon: Sparkles },
];

const VocabularyPage = () => {
  const { user, updateUserStats } = useAuth();
  const { data: unknownItems, isLoading: unknownLoading } = useQuery(
    'unknown_words',
    'list',
    async () => {
      const res = await unknownWordsAPI.list();
      return res.data?.items || [];
    },
    { staleTime: STALE.medium }
  );

  const knownByWord = useMemo(() => {
    const map = {};
    (unknownItems || []).forEach((it) => {
      if (it?.word) {
        map[it.word] = it.id;
        map[it.word.trim().toLowerCase()] = it.id;
      }
    });
    return map;
  }, [unknownItems]);

  const [vocabSet, setVocabSet] = useState('a1');
  const [mode, setMode] = useState('browse');
  const [learningFilter, setLearningFilter] = useState('unlearned'); // 'unlearned' (Për të mësuar - default!), 'known', 'all'
  const [sessionNewlyMarked, setSessionNewlyMarked] = useState(() => new Set());
  const [selectedChapter, setSelectedChapter] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState('all');
  const [search, setSearch] = useState('');
  const [includePhrases, setIncludePhrases] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const setInfo = VOCAB_SETS[vocabSet];
  const isSchritte = vocabSet === 'a1' || vocabSet === 'a2' || vocabSet === 'b1';

  const courses = getCourses(vocabSet);
  const schritteLessons = getLessons(vocabSet, selectedCourse);
  const chapter = B2_ASPEKTE_CHAPTERS.find((c) => c.id === selectedChapter);
  const modules = chapter?.modules ?? [];

  const filterOptions = useMemo(() => {
    if (isSchritte) {
      return {
        courseId: selectedCourse === 'all' ? null : selectedCourse,
        lessonIds: selectedLesson === 'all' ? null : [selectedLesson],
      };
    }
    return {
      chapterIds: selectedChapter === 'all' ? null : [selectedChapter],
      moduleId: selectedModule === 'all' ? null : selectedModule,
      includePhrases,
    };
  }, [isSchritte, selectedCourse, selectedLesson, selectedChapter, selectedModule, includePhrases]);

  const allEntries = useMemo(() => getEntries(vocabSet, filterOptions), [vocabSet, filterOptions]);
  const stats = useMemo(() => getStats(vocabSet), [vocabSet]);

  // Overall level statistics
  const allLevelEntries = useMemo(() => getEntries(vocabSet), [vocabSet]);
  const totalInSet = allLevelEntries.length;
  const knownCountInSet = useMemo(() => {
    return allLevelEntries.filter((e) => Boolean(knownByWord[e.de] || knownByWord[e.de?.trim()?.toLowerCase()])).length;
  }, [allLevelEntries, knownByWord]);
  const unlearnedCountInSet = Math.max(0, totalInSet - knownCountInSet);

  // Current view statistics (e.g. for current chapter/lesson)
  const totalInView = allEntries.length;
  const knownInView = useMemo(() => {
    return allEntries.filter((e) => Boolean(knownByWord[e.de] || knownByWord[e.de?.trim()?.toLowerCase()])).length;
  }, [allEntries, knownByWord]);
  const unlearnedInView = Math.max(0, totalInView - knownInView);

  const totalKnownCount = (unknownItems || []).length;

  const filtered = useMemo(() => {
    let list = allEntries;

    if (learningFilter === 'unlearned') {
      list = list.filter((e) => {
        const isKnown = Boolean(knownByWord[e.de] || knownByWord[e.de?.trim()?.toLowerCase()]);
        // If known, keep only if it was newly marked in this active view so it doesn't jump away instantly
        if (isKnown && !sessionNewlyMarked.has(e.de)) {
          return false;
        }
        return true;
      });
    } else if (learningFilter === 'known') {
      list = list.filter((e) => Boolean(knownByWord[e.de] || knownByWord[e.de?.trim()?.toLowerCase()]));
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (e) =>
        e.de.toLowerCase().includes(q) ||
        (e.sq && e.sq.toLowerCase().includes(q)) ||
        (e.example && e.example.toLowerCase().includes(q))
    );
  }, [allEntries, learningFilter, knownByWord, sessionNewlyMarked, search]);

  const hasActiveFilters = Boolean(search)
    || learningFilter !== 'unlearned'
    || (isSchritte
      ? selectedCourse !== 'all' || selectedLesson !== 'all'
      : selectedChapter !== 'all' || selectedModule !== 'all' || !includePhrases);

  const resetFilters = () => {
    setSelectedChapter('all');
    setSelectedModule('all');
    setSelectedCourse('all');
    setSelectedLesson('all');
    setSearch('');
    setIncludePhrases(true);
    setLearningFilter('unlearned');
    setSessionNewlyMarked(new Set());
    setCurrentPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / VOCAB_PAGE_SIZE));
  const pageEntries = useMemo(() => {
    const start = (currentPage - 1) * VOCAB_PAGE_SIZE;
    return filtered.slice(start, start + VOCAB_PAGE_SIZE);
  }, [filtered, currentPage]);

  const [detailEntry, setDetailEntry] = useState(null);
  const [pendingWords, setPendingWords] = useState(() => new Set());
  const unknownKey = user?.id ? makeKey(user.id, 'unknown_words', 'list') : null;

  const [pdfLoading, setPdfLoading] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadVocabPdf(vocabSet);
    } catch (err) {
      console.error('Failed to generate vocabulary PDF:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSetChange = (id) => {
    setVocabSet(id);
    setSelectedChapter('all');
    setSelectedModule('all');
    setSelectedCourse('all');
    setSelectedLesson('all');
    setSearch('');
    setSessionNewlyMarked(new Set());
    setCurrentPage(1);
  };

  const toggleKnown = useCallback(
    async (entry) => {
      if (!entry?.de || pendingWords.has(entry.de)) return;
      const deKey = entry.de;
      const lowerKey = entry.de.trim().toLowerCase();
      const existingId = knownByWord[deKey] || knownByWord[lowerKey];
      setPendingWords((s) => new Set([...s, entry.de]));

      const previous = unknownItems || [];
      if (existingId) {
        setQueryData(
          unknownKey,
          previous.filter((it) => it.id !== existingId && it.word !== entry.de)
        );
        setSessionNewlyMarked((prev) => {
          const next = new Set(prev);
          next.delete(entry.de);
          return next;
        });
      } else {
        const optimisticItem = {
          id: `temp-${Date.now()}`,
          word: entry.de,
          context: entry.sq || null,
          level: vocabSet.toUpperCase(),
          status: 'known',
        };
        setQueryData(unknownKey, [optimisticItem, ...previous]);
        setSessionNewlyMarked((prev) => new Set(prev).add(entry.de));
      }

      try {
        if (existingId) {
          if (typeof existingId === 'number' || (typeof existingId === 'string' && !existingId.startsWith('temp-'))) {
            await unknownWordsAPI.remove(existingId);
          } else {
            await unknownWordsAPI.removeByWord(entry.de);
          }
        } else {
          await unknownWordsAPI.add({
            word: entry.de,
            context: entry.sq || null,
            level: vocabSet.toUpperCase(),
            notes: entry.example || null,
            status: 'known',
          });
        }
        invalidateQueries('unknown_words');
        invalidateQueries('dashboard_stats');
      } catch (err) {
        console.error('Failed to toggle known word:', err);
        setQueryData(unknownKey, previous);
      } finally {
        setPendingWords((s) => {
          const n = new Set(s);
          n.delete(entry.de);
          return n;
        });
      }
    },
    [knownByWord, unknownItems, unknownKey, pendingWords, vocabSet]
  );

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Unified Page Header */}
        <PageHeader
          icon={GraduationCap}
          badge="Fjalor & Vocab"
          title={setInfo.title}
          description="Zgjidh nivelin dhe leksionin që dëshiron të studiosh. Fjalët që i di mund t'i shënosh me butonin 'E di' që të mësosh vetëm fjalët e panjohura."
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-xs font-bold text-zinc-700">
              {stats.chapters} {setInfo.unitsLabel}
            </span>
            <span className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-xs font-bold text-zinc-700">
              {stats.total} fjalë në nivel
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              {knownCountInSet} të ditura
            </span>
            <span className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">
              {unlearnedCountInSet} për të mësuar
            </span>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              title={`Shkarko të gjitha fjalët e ${setInfo.level} (Gjermanisht – Shqip) si PDF`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#DD0000]/30 bg-[#DD0000] px-3 py-1 text-xs font-bold text-white transition hover:bg-[#b80000] disabled:cursor-wait disabled:opacity-70 cursor-pointer sm:ml-auto"
            >
              {pdfLoading ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Shkarko PDF ({setInfo.level})
            </button>
            <Link
              to="/unknown-words"
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-900 bg-zinc-950 px-3 py-1 text-xs font-bold text-white transition hover:bg-zinc-800"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Fjalët e Ditura ({totalKnownCount}) →
            </Link>
          </div>
        </PageHeader>

        {/* Level Switcher (A1, A2, B1, B2) */}
        <SegmentedTabs
          ariaLabel="Niveli"
          tabs={Object.values(VOCAB_SETS).map((set) => ({
            id: set.id,
            label: `${set.level} — ${set.book.split(' ').slice(-2).join(' ')}`,
          }))}
          value={vocabSet}
          onChange={handleSetChange}
        />

        {/* Filters */}
        <section aria-label="Filtrat" className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
          <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_16rem_16rem]">
            <div className="relative min-w-0 sm:col-span-2 xl:col-span-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="search"
                aria-label="Kërko"
                placeholder="Kërko fjalë ose përkthim..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 w-full rounded-xl border border-zinc-200 bg-white pl-10 pr-3 text-sm text-zinc-900 shadow-sm transition placeholder:text-zinc-400 hover:border-zinc-300 focus:border-[#DD0000]/50 focus:outline-none focus:ring-4 focus:ring-[#DD0000]/10"
              />
            </div>

            {isSchritte ? (
              <>
                {courses.length > 1 ? (
                  <FilterSelect
                    label="Kursi"
                    value={selectedCourse}
                    onChange={(value) => {
                      setSelectedCourse(value);
                      setSelectedLesson('all');
                    }}
                  >
                    <option value="all">Të gjitha</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.level} · L{c.lessons[0]?.number}–{c.lessons[c.lessons.length - 1]?.number}
                      </option>
                    ))}
                  </FilterSelect>
                ) : (
                  <div className="hidden xl:block" aria-hidden="true" />
                )}
                <FilterSelect label="Leksioni" value={selectedLesson} onChange={setSelectedLesson}>
                  <option value="all">Të gjitha</option>
                  {schritteLessons.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.title} ({l.words.length})
                    </option>
                  ))}
                </FilterSelect>
              </>
            ) : (
              <>
                <FilterSelect
                  label="Kapitulli"
                  value={selectedChapter}
                  onChange={(value) => {
                    setSelectedChapter(value);
                    setSelectedModule('all');
                  }}
                >
                  <option value="all">Të gjithë</option>
                  {B2_ASPEKTE_CHAPTERS.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </FilterSelect>
                <FilterSelect
                  label="Moduli"
                  value={selectedModule}
                  onChange={setSelectedModule}
                  disabled={selectedChapter === 'all'}
                >
                  <option value="all">Të gjithë</option>
                  {modules.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title} ({m.words.length})
                    </option>
                  ))}
                </FilterSelect>
              </>
            )}
          </div>

          <div className="flex min-h-[49px] flex-wrap items-center justify-between gap-2 border-t border-zinc-100 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setLearningFilter('unlearned');
                  setSessionNewlyMarked(new Set());
                  setCurrentPage(1);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all cursor-pointer',
                  learningFilter === 'unlearned'
                    ? 'bg-zinc-950 text-white shadow-2xs'
                    : 'border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
                )}
              >
                Për të mësuar ({unlearnedInView})
              </button>
              <button
                type="button"
                onClick={() => {
                  setLearningFilter('known');
                  setCurrentPage(1);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all cursor-pointer',
                  learningFilter === 'known'
                    ? 'border border-emerald-300 bg-emerald-600 text-white shadow-2xs'
                    : 'border border-zinc-200 bg-white text-emerald-800 hover:border-emerald-300 hover:bg-emerald-50/60'
                )}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Të ditura ({knownInView})
              </button>
              <button
                type="button"
                onClick={() => {
                  setLearningFilter('all');
                  setCurrentPage(1);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all cursor-pointer',
                  learningFilter === 'all'
                    ? 'bg-zinc-950 text-white shadow-2xs'
                    : 'border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
                )}
              >
                Të gjitha ({totalInView})
              </button>

              {!isSchritte && (
                <FilterChip active={includePhrases} onClick={() => setIncludePhrases((v) => !v)}>
                  Përfshi shprehjet
                </FilterChip>
              )}
            </div>

            <div className="flex items-center gap-3">
              {learningFilter === 'unlearned' && sessionNewlyMarked.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSessionNewlyMarked(new Set())}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800 transition hover:bg-emerald-100 cursor-pointer"
                  title="Fshih nga kjo pamje fjalët që sapo i shënove si të ditura"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  Fshih të sapo-diturat ({sessionNewlyMarked.size})
                </button>
              )}
              <span className="text-xs text-zinc-500">
                <strong className="font-semibold text-zinc-900 tabular-nums">{filtered.length}</strong> fjalë
              </span>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" /> Pastro
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Mode Switcher Segmented Bar */}
        <SegmentedTabs
          ariaLabel="Mënyra e mësimit"
          tabs={MODES}
          value={mode}
          onChange={setMode}
        />

        {/* View Mode: Browse List */}
        {mode === 'browse' && (
          <div className="space-y-4">
            <BrowseList
              entries={pageEntries}
              totalResults={filtered.length}
              isSchritte={isSchritte}
              onToggleKnown={toggleKnown}
              onOpen={setDetailEntry}
              knownByWord={knownByWord}
              unknownLoading={unknownLoading}
              selectedChapter={selectedChapter}
              learningFilter={learningFilter}
              onSetLearningFilter={setLearningFilter}
            />
            {pageCount > 1 && (
              <nav
                aria-label="Paginimi i fjalorit"
                className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-zinc-200/80 bg-white px-5 py-3 shadow-sm sm:flex-row"
              >
                <span className="text-xs text-zinc-500">
                  Shfaqen {(currentPage - 1) * VOCAB_PAGE_SIZE + 1}–
                  {Math.min(currentPage * VOCAB_PAGE_SIZE, filtered.length)} nga {filtered.length} rezultate
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 cursor-pointer"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Mbrapa
                  </button>
                  <span className="min-w-20 text-center text-xs font-bold text-zinc-800">
                    Faqja {currentPage} nga {pageCount}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage === pageCount}
                    onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                    className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 cursor-pointer"
                  >
                    Tjetra
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </nav>
            )}
          </div>
        )}

        {/* View Mode: Flashcards */}
        {mode === 'flashcards' && (
          <Flashcards
            entries={filtered}
            isSchritte={isSchritte}
            onToggleKnown={toggleKnown}
            knownByWord={knownByWord}
          />
        )}

        {/* View Mode: Quiz */}
        {mode === 'quiz' && (
          <QuizMode entries={filtered} user={user} updateUserStats={updateUserStats} />
        )}

        <WordDetailsModal
          entry={detailEntry}
          setId={vocabSet}
          open={Boolean(detailEntry)}
          onOpenChange={(o) => {
            if (!o) setDetailEntry(null);
          }}
          saved={Boolean(detailEntry && (knownByWord[detailEntry.de] || knownByWord[detailEntry.de?.trim()?.toLowerCase()]))}
          pending={Boolean(detailEntry && pendingWords.has(detailEntry.de))}
          onToggleUnknown={() => toggleKnown(detailEntry)}
        />
      </div>
    </DashboardLayout>
  );
};

// Compact row: German word, translation, audio and known-word toggle.
const VocabRow = ({ entry, isKnown, disabled, onToggleKnown, onOpen }) => (
  <div
    className={cn(
      'group flex items-center justify-between gap-3 rounded-xl border p-3 shadow-2xs transition',
      isKnown
        ? 'border-emerald-200/90 bg-emerald-50/30 hover:border-emerald-300 hover:bg-emerald-50/50'
        : 'border-zinc-200/80 bg-white hover:border-zinc-300 hover:bg-zinc-50/50'
    )}
  >
    <button
      type="button"
      onClick={() => onOpen(entry)}
      className="flex min-h-11 min-w-0 flex-1 flex-col items-start gap-1 text-left sm:flex-row sm:items-center sm:gap-6 cursor-pointer"
    >
      <div className="flex min-w-0 items-center gap-2 sm:flex-1">
        <span
          className={cn(
            'break-words text-sm font-bold transition',
            isKnown ? 'text-zinc-900' : 'text-zinc-900 group-hover:text-[#DD0000]'
          )}
        >
          {entry.de}
        </span>
        {entry.module && (
          <span className="hidden shrink-0 rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-extrabold uppercase text-zinc-600 sm:inline">
            M{entry.module}
          </span>
        )}
        {entry.lesson && (
          <span className="hidden shrink-0 rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-extrabold uppercase text-zinc-600 sm:inline">
            L{entry.lesson}
          </span>
        )}
        {entry.type === 'phrase' && (
          <span className="hidden shrink-0 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-extrabold text-amber-800 sm:inline">
            Shprehje
          </span>
        )}
        {isKnown && (
          <span className="hidden shrink-0 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 sm:inline-flex">
            ✓ E ditur
          </span>
        )}
      </div>
      <span className="break-words text-sm text-zinc-500 sm:max-w-xs sm:text-right">
        {entry.sq || <span className="italic text-zinc-300">—</span>}
      </span>
    </button>
    <div className="flex items-center gap-1.5 shrink-0">
      <SpeakButton text={entry.de} lang="de-DE" className="h-9 w-9 shrink-0" />
      <KnownWordToggle
        isKnown={isKnown}
        disabled={disabled}
        onClick={() => onToggleKnown(entry)}
        className="h-9 min-w-9"
      />
    </div>
  </div>
);

const BrowseList = ({
  entries,
  totalResults,
  isSchritte,
  onToggleKnown,
  onOpen,
  knownByWord,
  unknownLoading,
  selectedChapter,
  learningFilter,
  onSetLearningFilter,
}) => {
  if (entries.length === 0) {
    if (learningFilter === 'unlearned') {
      return (
        <div className="text-center py-16 text-zinc-500 rounded-2xl border border-zinc-200/80 bg-white p-8 space-y-3 shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="font-extrabold text-base text-zinc-900">Bravo! I di të gjitha fjalët e kësaj pjese! 🎉</h3>
          <p className="text-xs text-zinc-500 max-w-md mx-auto">
            Të gjitha fjalët e këtij leksioni janë shënuar si të ditura. Mund të kalosh te leksioni i radhës ose t&apos;i rishikosh ato.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => onSetLearningFilter && onSetLearningFilter('all')}
              className="rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 cursor-pointer"
            >
              Shfaq të gjitha fjalët
            </button>
            <button
              type="button"
              onClick={() => onSetLearningFilter && onSetLearningFilter('known')}
              className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100 cursor-pointer"
            >
              Shfaq fjalët e ditura
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-16 text-zinc-400 rounded-2xl border border-zinc-200/80 bg-white p-8">
        <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-semibold text-sm">Nuk u gjet asnjë fjalë.</p>
      </div>
    );
  }

  if (!isSchritte) {
    if (selectedChapter === 'all') {
      const groups = [];
      const seenChapterIds = new Map();

      entries.forEach((entry) => {
        const key = entry.chapterId ?? 'unknown_chapter';
        if (!seenChapterIds.has(key)) {
          seenChapterIds.set(key, groups.length);
          groups.push({
            chapterId: key,
            chapterNumber: entry.chapterNumber,
            words: [],
          });
        }
        groups[seenChapterIds.get(key)].words.push(entry);
      });

      return (
        <div className="space-y-6">
          <p className="text-xs text-zinc-400 font-extrabold uppercase tracking-wider">
            {totalResults} fjalë të filtruara
          </p>
          {groups.map((group) => (
            <div key={group.chapterId} className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-xs font-black uppercase tracking-wider text-zinc-900 bg-zinc-100 px-3 py-1 rounded-md">
                  Kapitel {group.chapterNumber}
                </span>
                <span className="text-xs text-zinc-400">{group.words.length} fjalë</span>
                <div className="flex-1 h-px bg-zinc-100" />
              </div>

              <div className="space-y-1.5">
                {group.words.map((entry, i) => (
                  <VocabRow
                    key={`${entry.de}-${i}`}
                    entry={entry}
                    isKnown={Boolean(knownByWord[entry.de] || knownByWord[entry.de?.trim()?.toLowerCase()])}
                    disabled={unknownLoading}
                    onToggleKnown={onToggleKnown}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      const groups = [];
      const seenModuleIds = new Map();

      entries.forEach((entry) => {
        const key = entry.moduleId ?? '__phrases__';
        if (!seenModuleIds.has(key)) {
          seenModuleIds.set(key, groups.length);
          groups.push({
            moduleId: key,
            moduleNumber: entry.module,
            moduleTitle: entry.moduleTitle,
            words: [],
          });
        }
        groups[seenModuleIds.get(key)].words.push(entry);
      });

      const getModuleLabel = (group) => {
        if (group.moduleId === '__phrases__') return 'Shprehje';
        if (group.moduleNumber === 5) return 'Wichtige Wortverbindungen';
        return group.moduleNumber ? `Modul ${group.moduleNumber}` : 'Fjalë';
      };

      return (
        <div className="space-y-6">
          <p className="text-xs text-zinc-400 font-extrabold uppercase tracking-wider">
            {totalResults} fjalë të filtruara
          </p>
          {groups.map((group) => (
            <div key={group.moduleId} className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-xs font-black uppercase tracking-wider text-zinc-900 bg-zinc-100 px-3 py-1 rounded-md">
                  {getModuleLabel(group)}
                </span>
                <span className="text-xs text-zinc-400">{group.words.length} fjalë</span>
                <div className="flex-1 h-px bg-zinc-100" />
              </div>

              <div className="space-y-1.5">
                {group.words.map((entry, i) => (
                  <VocabRow
                    key={`${entry.de}-${i}`}
                    entry={entry}
                    isKnown={Boolean(knownByWord[entry.de] || knownByWord[entry.de?.trim()?.toLowerCase()])}
                    disabled={unknownLoading}
                    onToggleKnown={onToggleKnown}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  // Schritte flat list
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-400 font-extrabold uppercase tracking-wider mb-2">
        {totalResults} fjalë të filtruara
      </p>
      {entries.map((entry, i) => (
        <VocabRow
          key={`${entry.de}-${i}`}
          entry={entry}
          isKnown={Boolean(knownByWord[entry.de] || knownByWord[entry.de?.trim()?.toLowerCase()])}
          disabled={unknownLoading}
          onToggleKnown={onToggleKnown}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
};

const Flashcards = ({ entries, isSchritte, onToggleKnown, knownByWord }) => {
  const [deck, setDeck] = useState(() => shuffleArray(entries));
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(new Set());

  const reshuffle = useCallback(() => {
    setDeck(shuffleArray(entries));
    setIndex(0);
    setFlipped(false);
    setKnown(new Set());
  }, [entries]);

  useEffect(() => {
    reshuffle();
  }, [entries, reshuffle]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-400 rounded-2xl border border-zinc-200/80 bg-white p-8">
        <p className="font-bold text-sm text-zinc-700">Nuk ka asnjë fjalë në këtë listë.</p>
        <p className="text-xs text-zinc-400 mt-1">Zgjidhni një leksion tjetër ose ndryshoni filtrin.</p>
      </div>
    );
  }

  const card = deck[index];
  const progress = `${index + 1} / ${deck.length}`;
  const isKnown = Boolean(card && (knownByWord?.[card.de] || knownByWord?.[card.de?.trim()?.toLowerCase()]));

  const next = () => {
    setFlipped(false);
    setIndex((i) => (i + 1 >= deck.length ? 0 : i + 1));
  };

  const prev = () => {
    setFlipped(false);
    setIndex((i) => (i - 1 < 0 ? deck.length - 1 : i - 1));
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
          {progress} • {known.size} të mësuara
        </span>
        <button
          onClick={reshuffle}
          className="flex items-center gap-1.5 text-xs font-bold text-zinc-600 hover:text-zinc-900 transition cursor-pointer"
        >
          <RotateCw className="w-3.5 h-3.5" /> Përziej
        </button>
      </div>

      <div
        className="w-full min-h-[260px] cursor-pointer rounded-2xl border border-zinc-200/80 bg-white p-8 text-center shadow-sm transition hover:border-zinc-300 flex flex-col items-center justify-center relative overflow-hidden"
        onClick={() => setFlipped(!flipped)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFlipped(!flipped);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div aria-hidden="true" className="absolute top-0 inset-x-0 flex h-1">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>

        <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400 mb-4">
          {flipped ? (card.sq ? 'Përkthimi Shqip' : 'Shprehje') : 'Gjermanisht'}
        </span>
        <div className="text-2xl font-black text-zinc-900 leading-relaxed flex items-center gap-2 justify-center">
          <span>{flipped ? card.sq || card.de : card.de}</span>
          {(!flipped || !card.sq) && <SpeakButton text={card.de} lang="de-DE" />}
        </div>
        <span className="text-xs text-zinc-400 mt-6 font-medium">Kliko për ta rrotulluar</span>
      </div>

      <div className="flex justify-center">
        <KnownWordToggle isKnown={isKnown} onClick={() => onToggleKnown(card)} />
      </div>

      {flipped ? (
        <div className="space-y-2 pt-2">
          <p className="text-center text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
            Vlerëso kujtesën (Sistemi SRS):
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { grade: SRS_GRADES.AGAIN, ...GRADE_LABELS[SRS_GRADES.AGAIN] },
              { grade: SRS_GRADES.HARD, ...GRADE_LABELS[SRS_GRADES.HARD] },
              { grade: SRS_GRADES.GOOD, ...GRADE_LABELS[SRS_GRADES.GOOD] },
              { grade: SRS_GRADES.EASY, ...GRADE_LABELS[SRS_GRADES.EASY] },
            ].map(({ grade, label, time, color }) => (
              <button
                key={grade}
                type="button"
                onClick={() => {
                  reviewWord(card.de, grade);
                  if (grade >= SRS_GRADES.GOOD) {
                    setKnown((k) => new Set([...k, card.de]));
                  }
                  next();
                }}
                className={`py-2 px-3 rounded-xl text-xs font-bold transition shadow-2xs flex flex-col items-center justify-center ${color}`}
              >
                <span>{label}</span>
                <span className="text-[10px] opacity-75 font-normal">({time})</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={prev}
            className="flex-1 rounded-xl border border-zinc-200 bg-white py-2.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 inline-flex items-center justify-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Mbrapa
          </button>
          <button
            type="button"
            onClick={() => setFlipped(true)}
            className="flex-1 rounded-xl bg-zinc-950 py-2.5 text-xs font-bold text-white hover:bg-zinc-800"
          >
            Shiko Përgjigjen
          </button>
          <button
            type="button"
            onClick={next}
            className="flex-1 rounded-xl border border-zinc-200 bg-white py-2.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 inline-flex items-center justify-center gap-1"
          >
            Tjetra <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const QuizMode = ({ entries, user, updateUserStats }) => {
  const quizEntries = useMemo(() => entries.filter((e) => e.sq), [entries]);
  const [started, setStarted] = useState(false);
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [finished, setFinished] = useState(false);
  const TOTAL = Math.min(15, quizEntries.length);

  const start = () => {
    if (quizEntries.length < 4) return;
    setDeck(shuffleArray(quizEntries).slice(0, TOTAL));
    setIndex(0);
    setScore(0);
    setFeedback(null);
    setFinished(false);
    setStarted(true);
  };

  const current = deck[index];

  const options = useMemo(() => {
    if (!current) return [];
    const wrong = shuffleArray(quizEntries.filter((e) => e.de !== current.de)).slice(0, 3);
    return shuffleArray([current, ...wrong]);
  }, [current, quizEntries]);

  const handleAnswer = (option) => {
    if (feedback) return;
    const correct = option.de === current.de;
    const newScore = correct ? score + 10 : score;
    if (correct) setScore(newScore);
    setFeedback(correct ? 'correct' : 'wrong');

    setTimeout(() => {
      if (index + 1 >= deck.length) {
        finishQuiz(newScore);
      } else {
        setIndex((i) => i + 1);
        setFeedback(null);
      }
    }, 1000);
  };

  const finishQuiz = async (finalScore) => {
    const totalXp = (user?.xp || 0) + finalScore;
    if (updateUserStats) {
      await updateUserStats({ xp: totalXp });
    }
    setScore(finalScore);
    setFinished(true);
  };

  if (quizEntries.length < 4) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <p>Duhen të paktën 4 fjalë për të filluar kuizin.</p>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="max-w-md mx-auto rounded-2xl border border-zinc-200/80 bg-white p-8 text-center shadow-sm space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
          <Sparkles size={28} />
        </div>
        <h2 className="text-lg font-bold text-zinc-900">Kuiz i Shpejtë i Fjalorit</h2>
        <p className="text-xs text-zinc-500">
          {TOTAL} pyetje — zgjidh përkthimin e saktë shqip për fjalën gjermane.
        </p>
        <button
          type="button"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-5 py-2.5 text-xs font-bold text-white hover:bg-[#DD0000] transition"
        >
          Fillo Kuizin Tani <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="max-w-md mx-auto rounded-2xl border border-zinc-200/80 bg-white p-8 text-center shadow-sm space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
          <Trophy size={28} />
        </div>
        <h2 className="text-xl font-extrabold text-zinc-900">Kuizi Përfundoi!</h2>
        <p className="text-3xl font-black text-zinc-900">+{score} XP</p>
        <p className="text-xs text-zinc-500">
          {Math.round((score / (TOTAL * 10)) * 100)}% saktësi
        </p>
        <button
          type="button"
          onClick={start}
          className="rounded-xl bg-zinc-950 px-5 py-2.5 text-xs font-bold text-white hover:bg-[#DD0000] transition"
        >
          Provo Përsëri
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div className="flex justify-between items-center">
        <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
          Pyetja {index + 1} / {deck.length}
        </span>
        <span className="text-xs font-extrabold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-md">
          {score} XP
        </span>
      </div>

      <div
        className={`rounded-2xl border p-8 text-center shadow-sm transition ${
          feedback === 'correct'
            ? 'border-emerald-300 bg-emerald-50/50'
            : feedback === 'wrong'
            ? 'border-red-300 bg-red-50/50'
            : 'border-zinc-200/80 bg-white'
        }`}
      >
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400 block mb-2">
          Çfarë do të thotë?
        </span>
        <p className="text-2xl font-black text-zinc-900">{current.de}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {options.map((opt, i) => (
          <button
            key={i}
            onClick={() => handleAnswer(opt)}
            disabled={!!feedback}
            className={`rounded-xl border p-4 text-xs font-bold text-left transition ${
              feedback && opt.de === current.de
                ? 'border-emerald-500 bg-emerald-50 text-emerald-900 font-extrabold'
                : feedback && opt.de !== current.de
                ? 'border-zinc-200/60 bg-zinc-50 text-zinc-400 opacity-50'
                : 'border-zinc-200 bg-white text-zinc-800 hover:border-zinc-300 hover:bg-zinc-50'
            }`}
          >
            {opt.sq}
          </button>
        ))}
      </div>

      {feedback === 'wrong' && (
        <p className="text-center text-xs text-red-600 font-semibold">
          Përgjigja e saktë: {current.sq}
        </p>
      )}
    </div>
  );
};

export default VocabularyPage;
